import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, startWith, takeUntil } from 'rxjs/operators';
import { merge, ReplaySubject } from 'rxjs';
import { FormHelper } from '@helpers/form.helper';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { EntityModel } from '@data-layer/models/entities';
import { ShipmentModel } from '@data-layer/models/common';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';

@Component({
  selector: 'es-entity-unify-default-desktop',
  template: `
    <p class="sidebar-form-description">{{ info?.subTitleDesktop | translate }}</p>

    <span class="sidebar-form-hint" *ngIf="isShowInheritedEntity">{{ info?.subTitleHint | translate }}</span>

    <form autocomplete="off" [formGroup]="formGroup" (ngSubmit)="submit()">
      <app-entity-unify-card
        *ngIf="isShowInheritedEntity"
        [entity]="inheritedEntity"
        [formGroup]="formGroup"
        [code]="entityCode"
        [selectedValueControl]="radioButton"
        [isDesktop]="true"
      ></app-entity-unify-card>

      <span class="sidebar-form-hint mt-3" *ngIf="!!inheritedEntity && isShowInheritedEntity">
        {{ 'LABELS.OR_ADD_DETAILS' | translate }}
      </span>

      <app-entity-unify-update
        *ngIf="formGroup"
        [entity]="entity"
        [formGroup]="formGroup"
        [code]="entityCode"
        [selectedValueControl]="radioButton"
        [isDesktop]="true"
        [isResetCounty]="entityCode !== enumEntityModelCode.CONSIGNEE"
        (formCleared)="formCleared()"
      ></app-entity-unify-update>

      <div class="sidebar-form-buttons" *ngIf="!isHideFooter">
        <button type="submit" class="btn btn-primary">{{ 'BUTTON.SAVE' | translate }}</button>
      </div>
    </form>

    <missing-fields [code]="'LABELS.ENTRY_SUMMARY'" (decline)="submit(false)"></missing-fields>
  `,
  styleUrls: ['./es-entity-unify-default-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsEntityUnifyDefaultDesktopComponent implements OnInit, OnDestroy {
  @Input() public formGroup: UntypedFormGroup;
  @Input() public radioButton: AbstractControl;

  @Input() public info: {
    code: enumEntityModelCode;
    CODE: enumEntityModelCode; // TODO check & refactor later
    imgSrc: string;
    titleDesktop: string;
    subTitleDesktop: string;
    hint: string;
    addDetails: string;
    tooltip: string;
    addNew: string;
    subTitleHint: string;
  } = null;
  @Input() public isCanEdit = false;
  @Input() public isHideFooter: boolean;

  @Input() public entityCode: enumEntityModelCode;
  @Input() public entityKeyName: string;
  @Input() public inheritedEntity: EntityModel;
  @Input() public entity: EntityModel = new EntityModel({ id: 0 });
  @Input() public isNewInvoice: boolean;
  @Input() public currentShipment: ShipmentModel;
  @Input() public currentInvoice: InvoiceModel;
  @Output() public entityDataSaved: EventEmitter<EntityModel> = new EventEmitter();
  @Output() public editChange: EventEmitter<any> = new EventEmitter();

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  public code: enumEntityModelCode;
  public isShowMessage: boolean = false;
  public validationMessages: {
    [key: string]: {
      value: string;
      messages: string[];
    };
  };

  public addressErrors: string[] = [];

  public initialEntity: EntityModel;

  public enumIdentifierQualifier: typeof enumIdentifierQualifier = enumIdentifierQualifier;
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject();

  constructor(private fh: FormHelper, private cdr: ChangeDetectorRef) {}

  public get nameValue(): string {
    return this.fieldValue('name');
  }

  public ngOnInit(): void {
    this.entity = { ...this.entity, entityCode: this.entityCode };
    this.initialEntity = { ...this.entity };
    this.code = this.entityCode;

    // Handle radio button changes - when user selects "Use Importer as Buyer"
    this.radioButton.valueChanges
      .pipe(takeUntil(this.destroy$), distinctUntilChanged(), startWith(this.radioButton.value))
      .subscribe((value) => {
        if (value === this.inheritedEntity?.id) {
          // User selected "Use Importer as Buyer" - restore form with importer data
          this.restoreFormWithInheritedEntity();
        } else {
          // User selected custom form - restore with current entity data
          this.formGroup.patchValue(this.entity, { emitEvent: false });
        }
      });

    // Handle importer special case
    if (this.entityCode === enumEntityModelCode.IMPORTER && !this.isSavedFromCompany) {
      this.fh.patchIsfEntity(this.formGroup, this.entity, { emitEvent: false });
      this.radioButton.patchValue(0, { emitEvent: false });
      this.entity.id = 0;
    }

    // Listen to individual field changes (like ISF does) - this catches ALL changes including nested address fields
    merge(
      this.formGroup.get('entityIdentifier').valueChanges,
      this.formGroup.get('entityIdentifierQualifier').valueChanges,
      this.formGroup.get('name').valueChanges,
      this.formGroup.get('dateOfBirth').valueChanges,
      this.formGroup.get('address').valueChanges,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // When any field changes, uncheck the radio button if it was set to use inherited entity
        this.handleFormFieldChange();
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public formCleared(): void {
    this.radioButton.patchValue(0, { emitEvent: false });
    this.formGroup.patchValue({ id: 0, entityCode: this.entityCode }, { emitEvent: false });
    // Reset entity to a new empty entity to ensure clean state
    this.entity = new EntityModel({ id: 0, entityCode: this.entityCode });

    //#12484 - added state enable hotfix
    this.formGroup.get(['address', 'state']).enable({ emitEvent: false });

    // Trigger change detection to update UI
    this.cdr.markForCheck();
  }

  public submit(isNeedCheck: boolean = true): void {
    this.isShowMessage = false;
    const invalidFields = this.fh.getInvalidFieldsByCode(this.formGroup.value, this.entityCode);
    if (isNeedCheck && Object.keys(invalidFields).length > 0) {
      this.fh.markAsTouched(this.formGroup);
      this.openModal(invalidFields);
      this.cdr.markForCheck();
      return;
    }

    this.entityDataSaved.emit(this.formGroup.getRawValue());
  }

  public get isShowInheritedEntity(): boolean {
    return (
      !!this.inheritedEntity &&
      !(
        this.inheritedEntity.entityCode === enumEntityModelCode.IMPORTER &&
        this.inheritedEntity.entityIdentifierQualifier === enumIdentifierQualifier.ANI
      )
    );
  }

  private openModal(data) {
    this.missingFieldModal.openCustomModal(data);
  }

  private getFieldsConfig() {
    this.validationMessages = {};
    if (!this.currentInvoice || !this.currentInvoice[this.entityKeyName]) {
      return;
    }
    Object.keys(this.currentInvoice[this.entityKeyName]).forEach((fieldName: string) => {
      const value = this.currentInvoice[this.entityKeyName][fieldName];
      this.validationMessages[fieldName] = {
        value,
        messages: EntityValidationHelper.entityMapValidation.has(fieldName)
          ? EntityValidationHelper.entityMapValidation.get(fieldName)(this.currentInvoice[this.entityKeyName])
          : [],
      };
    });

    this.addressErrors = this.fieldErrors('address');
  }

  private fieldErrors(fieldName: string): string[] {
    if (!this.validationMessages || !this.validationMessages.hasOwnProperty(fieldName)) {
      return [];
    }
    const { messages } = this.validationMessages[fieldName];
    return messages && messages.length ? messages.map((message) => 'BACK_ERROR.' + message) : [];
  }

  private fieldValue(fieldName: string): string {
    if (!this.validationMessages || !this.validationMessages.hasOwnProperty(fieldName)) {
      return '';
    }
    const { value, messages } = this.validationMessages[fieldName];
    return messages && messages.length ? 'BACK_ERROR.' + messages[0] : value;
  }

  private hasError(fieldName: string): boolean {
    if (!this.validationMessages || !this.validationMessages.hasOwnProperty(fieldName)) {
      return false;
    }
    const { messages } = this.validationMessages[fieldName];
    return !!(messages && messages.length);
  }

  private get isSavedFromCompany(): boolean {
    const company = this.inheritedEntity;
    const savedEntity = this.entity;

    if (!company || !savedEntity) {
      return false;
    }

    return (
      company.entityIdentifier === savedEntity.entityIdentifier &&
      company.entityIdentifierQualifier === savedEntity.entityIdentifierQualifier &&
      company.name === savedEntity.name &&
      company?.address?.line1 === savedEntity?.address?.line1 &&
      company?.address?.line2 === savedEntity?.address?.line2 &&
      company?.address?.line3 === savedEntity?.address?.line3 &&
      company?.address?.city === savedEntity?.address?.city &&
      company?.address?.state?.id === savedEntity?.address?.state?.id &&
      company?.address?.postalCode === savedEntity?.address?.postalCode
    );
  }

  /**
   * TODO: refactor this :)
   * Restore form with inherited entity data (e.g., importer data for buyer)
   * This matches ISF's approach of patching with emitEvent: false
   */
  private restoreFormWithInheritedEntity(): void {
    if (!this.inheritedEntity) {
      return;
    }

    const identifierRequired = [enumEntityModelCode.IMPORTER, enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER].includes(
      this.entityCode,
    );

    // First, reset the form to ensure clean state before patching
    // This ensures that all modified values are cleared before restoring with inherited entity
    const currentId = this.formGroup.get('id').value;
    const currentEntityCode = this.formGroup.get('entityCode')?.value;
    this.formGroup.reset({ id: currentId, entityCode: currentEntityCode || this.entityCode }, { emitEvent: false });

    // Patch entity with emitEvent: false to prevent triggering formGroup.valueChanges
    this.fh.patchIsfEntity(this.formGroup, { ...this.inheritedEntity, id: this.inheritedEntity.id }, { emitEvent: false });

    // Patch address separately with emitEvent: false (like ISF does)
    if (!!this.inheritedEntity.address) {
      this.fh.patchAddress(this.formGroup.get('address') as UntypedFormGroup, this.inheritedEntity.address, true, { emitEvent: false });
    }

    // Update validators with emitEvent: false (like ISF does)
    this.fh.updateValidatorsByEntityIdentifierQual(this.formGroup, identifierRequired, this.inheritedEntity.entityIdentifierQualifier, {
      emitEvent: false,
    });

    // Explicitly set radioButton value to inheritedEntity.id to ensure UI updates
    // Use setValue instead of patchValue to ensure the value is set even if it's the same
    this.radioButton.setValue(this.inheritedEntity.id, { emitEvent: false });

    // Mark as pristine AFTER patching (since emitEvent: false prevents formGroup.valueChanges from firing)
    this.formGroup.markAsPristine();

    // Update this.entity to match the inherited entity - this is critical for subsequent restorations
    this.entity = new EntityModel({ ...this.inheritedEntity, entityCode: this.entityCode });

    // Trigger change detection to update the UI (especially the radio button visual state)
    this.cdr.markForCheck();
  }

  /**
   * TODO: refactor this :)
   * Handle form field changes - uncheck radio button when user modifies any field
   * This matches ISF's setFormIds() approach
   */
  private handleFormFieldChange(): void {
    // Skip if form is pristine (initial load or when radio button fills form with importer data)
    if (this.formGroup.pristine) {
      return;
    }

    // If radio button is currently set to use inherited entity, uncheck it
    if (this.radioButton.value === this.inheritedEntity?.id) {
      this.radioButton.patchValue(0, { emitEvent: false });
      this.formGroup.patchValue({ id: 0 }, { emitEvent: false });
      this.entity.id = 0;
    }

    // For importer, always uncheck if form is dirty
    if (enumEntityModelCode.IMPORTER === this.entityCode) {
      this.radioButton.patchValue(0, { emitEvent: false });
      this.entity.id = 0;
    }

    // For buyer/consignee/manufacturer, uncheck if initial entity was using inherited entity
    if (
      [enumEntityModelCode.CONSIGNEE, enumEntityModelCode.BUYER, enumEntityModelCode.MANUFACTURER].includes(this.entityCode) &&
      (!this.initialEntity?.id || this.initialEntity?.id === this.inheritedEntity?.id)
    ) {
      this.radioButton.patchValue(0, { emitEvent: false });
      this.formGroup.patchValue({ id: 0 }, { emitEvent: false });
      this.entity.id = 0;
    }
  }
}
